/* <notify-button> — captures email, fires Klaviyo "Subscribed to Drop" event.
   Contract: plans/drop-notification-integration.md §C.

   Hard requirements (do not deviate):
   - Metric name is the literal string "Subscribed to Drop".
   - drop_slug must equal data-drop-slug, which is the metaobject system.handle.
   - Never include event_properties.source — "system" is a server-only marker.
   - Never replace _learnq.push() with raw fetch('/client/events/'); raw drops the email
     for unidentified browser sessions. Klaviyo's queue handles identification.
   - Klaviyo's klaviyo.js is loaded site-wide by the Klaviyo Shopify app embed
     (config/settings_data.json). If that embed is removed, this silently breaks.
*/

(function () {
  const win = window;
  win._learnq = win._learnq || [];

  class NotifyButton extends HTMLElement {
    connectedCallback() {
      this.dropSlug = this.dataset.dropSlug;
      this.dropTitle = this.dataset.dropTitle;
      this.dropScheduledAt = this.dataset.dropScheduledAt;
      this.dropImageUrl = this.dataset.dropImageUrl;
      this.dropUrl = this.dataset.dropUrl;
      this.form = this.querySelector('form');
      if (!this.form) {
        console.warn('[notify-button] no <form> child; nothing to bind');
        return;
      }
      this.input = this.querySelector('input[type="email"]');
      this.submitBtn = this.querySelector('button[type="submit"]');

      if (this.alreadySubscribed()) {
        this.renderConfirmed();
        return;
      }
      this.form.addEventListener('submit', this.handleSubmit);
    }

    disconnectedCallback() {
      this.form && this.form.removeEventListener('submit', this.handleSubmit);
    }

    handleSubmit = (e) => {
      e.preventDefault();
      const email = this.input.value.trim();
      if (!email) {
        this.renderError('Enter an email address.');
        return;
      }

      this.submitBtn.disabled = true;
      try {
        win._learnq.push(['identify', { '$email': email }]);
        const props = {
          drop_slug: this.dropSlug,
          drop_title: this.dropTitle,
        };
        if (this.dropScheduledAt) props.drop_scheduled_at = this.dropScheduledAt;
        if (this.dropImageUrl)    props.drop_image_url    = this.dropImageUrl;
        if (this.dropUrl)         props.drop_url          = this.dropUrl;
        win._learnq.push(['track', 'Subscribed to Drop', props]);
        this.markSubscribed();
        this.renderConfirmed();
        window.dispatchEvent(new CustomEvent('drop:subscribed', {
          detail: { drop_slug: this.dropSlug, email }
        }));
      } catch (err) {
        console.error('[notify-button] subscribe failed', err);
        this.renderError("Couldn't save your spot — please try again.");
        this.submitBtn.disabled = false;
      }
    };

    alreadySubscribed() {
      try {
        const subscribed = JSON.parse(localStorage.getItem('drop_subscribed') || '[]');
        return subscribed.includes(this.dropSlug);
      } catch (_) {
        return false;
      }
    }

    markSubscribed() {
      try {
        const subscribed = JSON.parse(localStorage.getItem('drop_subscribed') || '[]');
        if (!subscribed.includes(this.dropSlug)) {
          subscribed.push(this.dropSlug);
          localStorage.setItem('drop_subscribed', JSON.stringify(subscribed));
        }
      } catch (_) { /* private mode / quota — UI nicety only, ignore */ }
    }

    renderConfirmed() {
      this.innerHTML = '<div class="notify-confirmed">We’ll let you know when we’re live.</div>';
    }

    renderError(msg) {
      let errEl = this.querySelector('.notify-error');
      if (!errEl) {
        errEl = document.createElement('div');
        errEl.className = 'notify-error';
        this.appendChild(errEl);
      }
      errEl.textContent = msg;
    }
  }

  if (!customElements.get('notify-button')) {
    customElements.define('notify-button', NotifyButton);
  }
})();
